import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { FileEdit, ArrowRight, Trash2, Zap, AlertCircle } from "lucide-react";
import type { LibraryDraft } from "@/hooks/useLibraryData";

function formatRelativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

function getProgressLabel(turnCount: number): string {
  // Builder has 4 steps: Goal, Context, Persona, Review
  if (turnCount <= 1) return 'Step 1 of 4 (Goal)';
  if (turnCount === 2) return 'Step 2 of 4 (Context)';
  if (turnCount === 3) return 'Step 3 of 4 (Persona)';
  return 'Step 4 of 4 (Review)';
}

interface DraftCardProps {
  draft: LibraryDraft;
  onContinue: (draft: LibraryDraft) => void;
  onDelete: (draft: LibraryDraft) => void;
  onScore?: (draft: LibraryDraft) => void;
  isScoring?: boolean;
  folderBadge?: string;
  selectMode?: boolean;
  selected?: boolean;
  onSelectToggle?: (draft: LibraryDraft) => void;
}

export function DraftCard({
  draft,
  onContinue,
  onDelete,
  onScore,
  isScoring = false,
  folderBadge,
  selectMode = false,
  selected = false,
  onSelectToggle,
}: DraftCardProps) {
  const isOrphan = draft.status === 'completed' && draft.final_prompt != null;

  const handleCardClick = () => {
    if (selectMode) {
      onSelectToggle?.(draft);
      return;
    }
    if (isOrphan && onScore) {
      onScore(draft);
    } else {
      onContinue(draft);
    }
  };

  return (
    <div
      className={`border-2 rounded-lg p-4 md:p-5 hover:bg-muted/10 transition-colors cursor-pointer ${
        isOrphan
          ? 'border-amber-500/40 hover:border-amber-500/60'
          : 'border-dashed border-border/50 hover:border-border'
      }`}
      onClick={handleCardClick}
    >
      <div className="flex items-start gap-3">
        {selectMode ? (
          <Checkbox
            checked={selected}
            className="mt-2"
            aria-label={`Select draft ${draft.initial_goal || "Untitled draft"}`}
            onClick={(e) => e.stopPropagation()}
            onCheckedChange={() => onSelectToggle?.(draft)}
          />
        ) : null}

        <div className={`p-2 rounded-lg ${isOrphan ? 'bg-amber-500/10' : 'bg-muted/50'}`}>
          {isOrphan ? (
            <AlertCircle className="h-5 w-5 text-amber-500" />
          ) : (
            <FileEdit className="h-5 w-5 text-muted-foreground" />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <span className={`text-xs font-medium uppercase tracking-wide ${
              isOrphan ? 'text-amber-500' : 'text-muted-foreground'
            }`}>
              {isOrphan ? 'Unscored' : 'Draft'}
            </span>
            {folderBadge && (
              <Badge variant="outline" className="text-xs uppercase tracking-wide">
                From {folderBadge}
              </Badge>
            )}
          </div>

          {/* Initial goal preview */}
          <p className="text-sm text-foreground leading-relaxed line-clamp-2 mb-3">
            "{draft.initial_goal || 'Untitled draft...'}"
          </p>

          {/* Metadata row */}
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <div className="flex items-center gap-3">
              <span>Started: {formatRelativeTime(draft.started_at)}</span>
              <span className="hidden sm:inline">•</span>
              <span className="hidden sm:inline">
                {isOrphan ? 'Completed — needs scoring' : getProgressLabel(draft.turn_count)}
              </span>
            </div>
          </div>
        </div>

        <div className="flex flex-shrink-0 items-center gap-2">
          {!selectMode ? (
            <>
              <Button
                variant="ghost"
                size="sm"
                className="gap-1 text-destructive hover:text-destructive"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(draft);
                }}
              >
                <Trash2 className="h-3.5 w-3.5" />
                Delete
              </Button>
              {isOrphan ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1 border-amber-500/50 text-amber-500 hover:bg-amber-500/10"
                  disabled={isScoring}
                  onClick={(e) => {
                    e.stopPropagation();
                    onScore?.(draft);
                  }}
                >
                  {isScoring ? (
                    <>Scoring...</>
                  ) : (
                    <>
                      <Zap className="h-3.5 w-3.5" />
                      Score now
                    </>
                  )}
                </Button>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    onContinue(draft);
                  }}
                >
                  Continue
                  <ArrowRight className="h-3.5 w-3.5" />
                </Button>
              )}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
